import { toast } from 'sonner';
import { getMode, getSub, applyProfiles, PROFILES, DETECT_SUBS } from './profiles';
import { BRANCH_KEYS, SUB_KEYS, branchOn, subOn } from './policy';

// voice/text agents operate the app through these client tools (fixed actions, no free clicking)
let ctx = { project: null, report: null, t: {} };
export const setAgentAppCtx = (c) => { ctx = c; };
const fire = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));
const ok = (s) => JSON.stringify({ ok: true, ...s });
const err = (e) => JSON.stringify({ ok: false, error: e });

// tool key -> [label, event]
const TOOLS = {
  branch_picker: ['Branche wählen', 'giss-profile-open'],
  moisture: ['Feuchte-Raster (Foto mit Messpunkten)', 'giss-moist'],
  facade: ['Fassaden-Studio', 'giss-facade-open'],
  photo_draw: ['Foto bemalen / beschriften', 'giss-photodraw-open'],
  gnss: ['GNSS / RTK Empfänger', 'giss-gnss-open'],
  cable_fault: ['Kabelfehler-Ortung', 'giss-fault-open'],
  tools: ['Werkzeug-Leiste', 'giss-drawer'],
  archive: ['Projektarchiv (PC)', 'giss-archive-open'],
  handoff: ['Tour Handy → PC', 'giss-handoff-open'],
  help: ['Bedienungsanleitung', 'giss-help'],
};
// show_button key -> data-testid
const BUTTONS = {
  camera: 'photo-fab-camera', gallery: 'gallery-btn', detail_photo: 'add-detail-photo-btn', note_photo: 'add-note-photo-btn',
  branch: 'mode-bar-btn', tools: 'fs-tools-tab', settings: 'settings-btn', new_report: 'new-report-btn', pdf: 'export-pdf-single-btn',
  map_layer: 'map-layer-select', report_title: 'report-title-input', report_text: 'report-text-input', project_title: 'project-title-input',
  project_description: 'project-desc-input', address: 'project-address-input', archive: 'archive-btn', catalog: 'catalog-open-btn',
};
const FIELDS = ['report_title', 'report_text', 'project_title', 'project_description'];
const exists = (id) => !!document.querySelector(`[data-testid="${id}"]`);

export function appState() {
  const { project, report, t } = ctx, m = getMode();
  return JSON.stringify({
    branch: m, branch_label: t['pf_' + m] || PROFILES[m]?.label || m, area: m === 'detect' ? getSub() : null,
    branches: BRANCH_KEYS.filter(branchOn).map((k) => ({ key: k, label: t['pf_' + k] || k })),
    areas: branchOn('detect') ? SUB_KEYS.filter((s) => subOn(s) && DETECT_SUBS[s]).map((s) => ({ key: s, label: t['dsub_' + s] || s })) : [],
    project: project ? { title: project.title, reports: project.reports?.length || 0, description: (project.description || '').slice(0, 200) } : null,
    report: report ? { title: report.title, photos: report.photos?.length || 0, text: (report.text || '').slice(0, 300) } : null,
    tools: Object.entries(TOOLS).map(([key, [label]]) => ({ key, label })),
    buttons: Object.keys(BUTTONS).filter((k) => exists(BUTTONS[k])),
    fields: report || project ? FIELDS : [],
    photo_targets: ['report', 'moisture'],
    moisture: (project?.moist || []).map((m) => ({ photo: m.name, unit: m.unit, areas: (m.areas || (m.rows ? [m] : [])).map((a) => ({ area: a.name, rows: a.rows, cols: a.cols, filled: Object.keys(a.vals || {}).length })) })),
    objects: (project?.fittings || []).filter((f) => f.name || f.caption).slice(0, 40).map((f) => ({ name: f.name || f.caption, type: f.type, bar: f.pIst ?? null })),
    selected_object: ctx.sel?.kind === 'fitting' ? (project?.fittings || []).find((f) => f.id === ctx.sel.id)?.name || 'unnamed object' : null,
  });
}

function switchBranch({ branch, area } = {}) {
  const b = String(branch || '').trim();
  if (!BRANCH_KEYS.includes(b)) return err(`unknown branch "${b}" - use a key from app_state.branches`);
  if (!branchOn(b)) return err('this branch is hidden on this device (settings / company admin)');
  if (b === 'detect' && area && !(SUB_KEYS.includes(area) && subOn(area))) return err(`area "${area}" is unknown or hidden - use a key from app_state.areas`);
  const s = b === 'detect' ? area || SUB_KEYS.find(subOn) : undefined;
  const prevM = getMode(), prevS = getSub();
  applyProfiles([b], s);
  undoable(`${ctx.t.agSwitched || 'Umgeschaltet'}: ${ctx.t['pf_' + b] || b}${s ? ` – ${ctx.t['dsub_' + s] || s}` : ''}`, () => applyProfiles([prevM], prevM === 'detect' ? prevS : undefined));
  return ok({ branch: b, area: s || null });
}

// tools that only exist in some branches: dialog testid + what to switch to first
const NEEDS = {
  moisture: ['moist-dialog', 'switch_branch building, or detect with area inhouse or roof'],
  facade: ['facade-dialog', 'switch_branch building'],
  cable_fault: ['fault-dialog', 'switch_branch detect with area cable'],
};
const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });
async function opened(key) {
  const n = NEEDS[key];
  if (!n) return null;
  await wait(450);
  return exists(n[0]) ? null : err(`this tool is not available in the current branch - first ${n[1]} (ask the user)`);
}

async function openTool({ tool } = {}) {
  const k = String(tool || '').trim(), e = TOOLS[k];
  if (!e) return err(`unknown tool "${tool}" - use a key from app_state.tools`);
  fire(e[1]);
  return (await opened(k)) || ok({ opened: k });
}

async function takePhoto({ target = 'report', hint = '' } = {}) {
  const tg = target === 'moisture' ? 'moisture' : 'report';
  if (tg === 'report' && !ctx.report) return err('no report open - create or open a report first');
  if (tg === 'moisture') { fire('giss-moist'); const no = await opened('moisture'); if (no) return no; }
  fire('giss-agent-ui', { kind: 'photo', target: tg, hint });
  return ok({ shown: 'camera button', note: 'the user must tap the glowing camera button once' });
}

function showButton({ target, hint = '' } = {}) {
  const id = BUTTONS[String(target || '').trim()];
  if (!id) return err(`unknown button "${target}" - use a key from app_state.buttons`);
  if (!exists(id)) return err('this button is not visible right now (other screen / branch)');
  fire('giss-agent-ui', { kind: 'point', testid: id, hint });
  return ok({ highlighted: target });
}

function fillField({ field, value } = {}) {
  const f = String(field || '').trim(), v = String(value ?? '').trim();
  if (!FIELDS.includes(f)) return err(`unknown field "${field}"`);
  if (!v) return err('empty value');
  if (f.startsWith('report') && !ctx.report) return err('no report open');
  if (f.startsWith('project') && !ctx.project) return err('no project open');
  const src = f.startsWith('report') ? ctx.report : ctx.project, prev = src[{ report_title: 'title', report_text: 'text', project_title: 'title', project_description: 'description' }[f]] || '';
  fire('giss-agent-fill', { field: f, value: v.slice(0, 4000) });
  fire('giss-agent-ui', { kind: 'point', testid: BUTTONS[f], hint: '' });
  undoable(`${ctx.t.agFilled || 'Eingetragen'}: ${v.slice(0, 60)}`, () => fire('giss-agent-fill', { field: f, value: prev, set: true }));
  return ok({ field: f, entered: v });
}

// every agent change can be undone from a short toast
function undoable(label, undo) {
  toast(label, { id: `ag-undo-${Date.now()}`, duration: 12000, action: { label: ctx.t.agUndo || 'Rückgängig', onClick: undo } });
}
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };
const norm = (s) => String(s || '').toLowerCase().replace(/[()[\].,;:#/_-]+/g, ' ').split(/\s+/).filter(Boolean);
// spoken names: every word of the query in the name, numbers must match exactly ("Hydrant 3", "Ortungspunkt 1")
const findBy = (list, name, key = 'name') => {
  const q = norm(name);
  if (!q.length) return null;
  const hit = (x) => { const w = norm(x[key]); return q.every((p) => (/^\d+$/.test(p) ? w.includes(p) : w.some((y) => y.startsWith(p)))); };
  return list.find((x) => String(x[key] || '').toLowerCase() === String(name).trim().toLowerCase()) || list.find(hit) || null;
};

// moisture grid: values in reading order into the next empty cells (or a given row/column, 1-based)
// values written in the same tick (React not re-rendered yet) – later calls build on them
const fresh = {};
function setMoisture({ value, row, col, photo, area, via } = {}) {
  const { project, updateProject } = ctx;
  if (via === 'agent' && !row && Date.now() - lastDict < 12000) return ok({ note: 'already entered by the app from the speech - do not enter again, do not repeat the values' });
  const raw = String(value ?? '').trim(), parts = !/\s/.test(raw) && (raw.match(/,/g) || []).length > 1 ? raw.split(',') : raw.split(/[\s;/]+|,(?=\s)/);
  const vals = parts.map(num).filter((v) => v != null);
  if (!vals.length) return err('no numeric value');
  const list = project?.moist || [];
  const m = findBy(list, photo) || [...list].sort((a, b) => (b.at || 0) - (a.at || 0))[0];
  if (!m) return err('no moisture photo yet - take_photo target moisture first');
  const areas = m.areas || (m.rows ? null : []);
  if (areas && !areas.length) return err('draw the wall area first: the user taps the 4 corners of the wall on the photo');
  const a = areas ? findBy(areas, area) || areas.find((x) => Object.keys(x.vals || {}).length < x.rows * x.cols) || areas[0] : m;
  const cells = [];
  for (let r = 0; r < a.rows; r++) for (let c = 0; c < a.cols; c++) cells.push(`${r}-${c}`);
  const fk = `${m.id}:${a.id}`, base = fresh[fk] && Date.now() - fresh[fk].at < 3000 ? fresh[fk].vals : a.vals || {};
  const r0 = num(row), c0 = num(col) || (r0 ? 1 : null), nv = { ...base }, done = [];
  let i = r0 && c0 ? cells.indexOf(`${r0 - 1}-${c0 - 1}`) : cells.findIndex((k) => nv[k] == null);
  if (i < 0) return err(r0 && c0 ? `row/col outside the grid (${a.rows} rows x ${a.cols} columns)` : 'all cells of this area are filled');
  const seq = !(r0 && c0);
  for (const v of vals) {
    while (seq && i < cells.length && nv[cells[i]] != null) i++;
    if (i >= cells.length) break;
    nv[cells[i]] = v; done.push(`${cells[i].split('-').map((x) => +x + 1).join('/')}=${v}`); i++;
  }
  const prev = base;
  const put = (vv) => updateProject((pr) => ({ ...pr, moist: (pr.moist || []).map((x) => (x.id !== m.id ? x : areas ? { ...x, areas: x.areas.map((y) => (y.id === a.id ? { ...y, vals: vv } : y)) } : { ...x, vals: vv })) }));
  put(nv);
  fresh[fk] = { vals: nv, at: Date.now() };
  undoable(`${ctx.t.agMoist || 'Feuchte'}: ${done.slice(0, 4).join(', ')}${done.length > 4 ? ` … (+${done.length - 4})` : ''}`, () => { delete fresh[fk]; put(prev); });
  const left = cells.filter((k) => nv[k] == null).length;
  return ok({ photo: m.name, area: a.name, entered: done, unit: m.unit, cells_left: left, next: left ? cells.find((k) => nv[k] == null).split('-').map((x) => +x + 1).join('/') : null });
}

// live dictation: numbers the user says while the moisture grid is open go straight into the next cells
const W = { null: 0, zero: 0, ein: 1, eins: 1, eine: 1, one: 1, zwei: 2, zwo: 2, two: 2, drei: 3, three: 3, vier: 4, four: 4, fünf: 5, fuenf: 5, five: 5, sechs: 6, sech: 6, six: 6, sieben: 7, sieb: 7, seven: 7, acht: 8, eight: 8, neun: 9, nine: 9, zehn: 10, ten: 10, elf: 11, eleven: 11, zwölf: 12, zwoelf: 12, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, zwanzig: 20, twenty: 20, dreißig: 30, dreissig: 30, thirty: 30, vierzig: 40, forty: 40, fünfzig: 50, fuenfzig: 50, fifty: 50, sechzig: 60, sixty: 60, siebzig: 70, seventy: 70, achtzig: 80, eighty: 80, neunzig: 90, ninety: 90, hundert: 100, hundred: 100 };
const FILL = new Set(['und', 'dann', 'ok', 'okay', 'weiter', 'so', 'jetzt', 'ja', 'next', 'and', 'then', 'nächster', 'nächste', 'wert', 'prozent', 'digits', 'digit']);
const STOP = /zeile|spalte|feld|reihe|row|col|riga|colonna|fila|falsch|korrig|lösch|rückg|undo|wrong|nicht|stop|fertig|ende/i;
const wordNum = (w) => {
  if (w in W) return W[w];
  let m = w.match(/^(.+?)zehn$/);
  if (m && W[m[1]] > 2 && W[m[1]] < 10) return 10 + W[m[1]];
  m = w.match(/^(.+?)und(.+)$/);
  return m && W[m[1]] > 0 && W[m[1]] < 10 && W[m[2]] % 10 === 0 && W[m[2]] >= 20 && W[m[2]] < 100 ? W[m[1]] + W[m[2]] : null;
};
export function speechNums(text) {
  const tk = String(text).toLowerCase().replace(/(\d)[,.](\d)/g, '$1.$2').split(/[\s,;:!?]+|(?<=\D)\.|\.(?=\D|$)/).filter(Boolean);
  const vals = [];
  let other = 0, dec = false;
  for (const w of tk) {
    const n = /^\d+(\.\d+)?$/.test(w) ? +w : wordNum(w);
    if (n != null) { if (dec && vals.length) { vals[vals.length - 1] = +`${Math.trunc(vals[vals.length - 1])}.${String(n)}`; dec = false; } else vals.push(n); } else if (/^(komma|punkt|point|virgola)$/.test(w)) dec = true; else if (!FILL.has(w)) other++;
  }
  return { vals, other };
}
let lastDict = 0;
const numW = (w) => (/^\d+$/.test(w) ? +w : wordNum(String(w).replace(/[.,]$/, '')));
// pressure by voice: "4,2" for the open object or "Hydrant 3 ist 4,2" by name
export function dictatePressure(text) {
  const m = String(text).match(/^(.*?\S)\s+(?:ist|hat|has|is|=|:)\s+(.+)$/i);
  const v = speechNums(m ? m[2] : text).vals[0];
  if (v == null) return null;
  const name = m && m[1].replace(/^(der|die|das|the)\s+/i, '').split(/\s+/).map((w) => (wordNum(w.toLowerCase()) ?? w)).join(' ');
  const r = JSON.parse(setPressure(name ? { object: name, value: v } : { value: v }));
  return r;
}
// returns a note for the agent when the app entered values itself, else null (agent handles the turn)
export function dictateMoisture(text0, raw = false) {
  if (!document.querySelector('[data-testid="moist-dialog"]')) return null;
  const text = String(text0).replace(/\b(fell|felt|fällt|fel|veld|welt)\b/gi, 'feld');
  // values and a correction in one phrase: handle the parts one after another
  const parts = text.split(/(?=\b(?:zeile|reihe|row)\b)/i).map((x) => x.trim()).filter(Boolean);
  if (parts.length > 1) { let last = null; for (const x of parts) last = dictateMoisture(x, raw) || last; return last; }
  // correction: "Zeile 2, Feld 3 ist 18"
  const cm = String(text).toLowerCase().match(/(?:zeile|reihe|row)\s+(\S+?)[\s,]+(?:feld|spalte|field|column|col)\s+(\S+?)[\s,]+(?:ist\s+|is\s+|=\s*)?(.+)$/);
  if (cm) {
    const rr = numW(cm[1]), cc = numW(cm[2]), v = speechNums(cm[3]).vals[0];
    if (!rr || !cc || v == null) return null;
    const r = JSON.parse(setMoisture({ value: String(v), row: rr, col: cc }));
    if (!r.ok) return null;
    lastDict = Date.now();
    const [nr, nc] = (r.next || `${rr}/${cc}`).split('/').map(Number);
    fire('giss-moist-next', { area: r.area, row: nr, col: nc });
    return raw ? { entered: r.entered, next: r.next, newRow: null, full: false, fix: true } : `APP DICTATION (already corrected by the app, do NOT call set_moisture): row ${rr} field ${cc} = ${v}. Say at most "ok".`;
  }
  if (STOP.test(text)) return null;
  const { vals, other } = speechNums(text);
  if (!vals.length || other > 2) return null;
  const r = JSON.parse(setMoisture({ value: vals.join(' ') }));
  if (!r.ok) return null;
  lastDict = Date.now();
  const lastRow = +String(r.entered.at(-1)).split('/')[0], nextRow = r.next ? +r.next.split('/')[0] : null;
  const [nr, nc] = (r.next || String(r.entered.at(-1)).split('=')[0]).split('/').map(Number);
  fire('giss-moist-next', { area: r.area, row: nr, col: nc });
  if (raw) return { entered: r.entered, next: r.next, newRow: r.next && nextRow > lastRow ? nextRow : null, full: !r.next };
  const say = !r.next ? 'All cells are filled - say so in a few words.' : nextRow > lastRow ? `A new row starts - say ONLY the new row, e.g. "Zeile ${nextRow}".` : 'Say NOTHING (or at most "ok"); do not repeat the values.';
  return `APP DICTATION (already entered by the app, do NOT call set_moisture for these): ${r.entered.join(', ')}; next cell ${r.next || '-'} (row/column). ${say}`;
}

// measured pressure (bar) on an object by name/number, else the selected object
function setPressure({ object, value } = {}) {
  const { project, updateProject, sel } = ctx, v = num(value);
  if (v == null || v < 0 || v > 100) return err('pressure value in bar missing or not plausible');
  const fits = project?.fittings || [];
  const f = object ? findBy(fits, object) || findBy(fits, object, 'caption') : sel?.kind === 'fitting' ? fits.find((x) => x.id === sel.id) : null;
  if (!f) return err(object ? `no object named "${object}"` : 'no object open - say its name or open it on the map');
  const prev = f.pIst;
  const put = (pIst) => updateProject((pr) => ({ ...pr, fittings: pr.fittings.map((x) => (x.id === f.id ? { ...x, pIst } : x)) }));
  put(v);
  undoable(`${f.name || f.caption || f.type}: ${String(v).replace('.', ',')} bar`, () => put(prev));
  return ok({ object: f.name || f.caption || f.type, bar: v });
}

// on-screen checklist of a guided workflow
function showSteps({ title, steps, current } = {}) {
  const list = String(steps || '').split(/\s*\|\s*|\n/).map((s) => s.trim()).filter(Boolean).slice(0, 12);
  if (!list.length) return err('steps missing');
  fire('giss-agent-steps', { title: String(title || ''), steps: list, current: Math.max(1, Math.min(list.length, num(current) || 1)) });
  return ok({ shown: list.length });
}

export const appTools = () => ({
  set_moisture: (p) => setMoisture({ ...p, via: 'agent' }),
  set_pressure: (p) => setPressure(p),
  show_steps: (p) => showSteps(p),
  app_state: () => appState(),
  switch_branch: (p) => switchBranch(p),
  open_tool: (p) => openTool(p),
  take_photo: (p) => takePhoto(p),
  show_button: (p) => showButton(p),
  fill_field: (p) => fillField(p),
});
window.__gissAgentApp = appTools;
window.__gissDictate = dictateMoisture;
